export const inspections = {
  shared: {
    jobVisibility: {
      contractors: "Contractors",
      team: "Team",
      private: "Private",
    },
    jobCategory: {
      Other: "Other",
      Cleaning: "Cleaning",
      Installation: "Installation",
      Replacement: "Replacement",
      Inspection: "Inspection",
      Maintenance: "Maintenance",
      Repair: "Repair",
    },
    /** A checklist template's built-in categories (`CHECKLIST_CATEGORIES`), spaces removed from the key. */
    checklistCategory: {
      Inspection: 'Inspection',
      Check: 'Check',
      Audit: 'Audit',
      SafetyReview: 'Safety Review',
      Maintenance: 'Maintenance',
      General: 'General',
    },
    /** components/ui/ProgressBar.tsx default caption. */
    itemsAnswered_one: '{{value}} of {{count}} item answered ({{percent}}%)',
    itemsAnswered_other: '{{value}} of {{count}} items answered ({{percent}}%)',
    /** lib/inspections/workflowRecommendation.ts — why a checklist or a cadence is suggested. */
    workflowRationale: {
      explicit: 'Chosen for this inspection.',
      lastUsed: 'Last used for this asset.',
      bestMatch: 'Best compatible workflow for this asset type.',
      history: "Based on this asset's last {{inspections}} ({{every}}).",
      historyInspections_one: '{{count}} inspection',
      historyInspections_other: '{{count}} inspections',
      historyEvery_one: 'about every {{count}} day',
      historyEvery_other: 'about every {{count}} days',
      assetTypeDefault: {
        car: 'Typical starting cadence for vehicle assets — adjust as needed.',
        property: 'Typical starting cadence for property assets — adjust as needed.',
        equipment: 'Typical starting cadence for equipment assets — adjust as needed.',
        other: 'Typical starting cadence for other assets — adjust as needed.',
      },
    },
    executionMode: {
      list: 'List',
      guidedGroup: 'Guided by group',
      guidedItem: 'Guided by item',
    },
    recordStatus: {
      inProgress: 'In progress',
      paused: 'Paused',
      submitted: 'Submitted',
      approved: 'Signed off',
      completed: 'Completed',
      cancelled: 'Cancelled',
    },
  },
  start: {
    planningLoadErrorTitle: "Couldn’t open planning",
    planningAssetUnavailable: "This asset isn’t available, or you no longer have access to it.",
    planningAssetMismatch: "This request is for a different asset. Open the request and try again.",
    planningLoading: "Getting things ready…",
    planningTitle: "Plan work",
    setupRequiredTitle: 'Setup required',
    setupRequiredBodyAll: 'Add at least one asset and one checklist before starting an inspection.',
    setupRequiredBodyAssets: 'Add at least one asset before starting an inspection.',
    setupRequiredBodyTemplates: 'Create at least one checklist before starting an inspection.',
    addAsset: 'Add asset',
    createChecklist: 'Create checklist',
    createChecklistA11y: 'Create checklist',
    createChecklistSheetTitle: 'Create checklist',
    createChecklistSheetSubtitle: 'Choose how you want to build your checklist.',
    createChecklistWithAi: 'Generate with AI',
    createChecklistManually: 'Create manually',
    overlayReturnFootnote: 'Opens in an overlay. When you save, you return here to start or schedule.',
    stepSelectAsset: '1. Select asset',
    searchAssetsPlaceholder: 'Search assets',
    searchAssetsA11y: 'Search assets',
    allTypesA11y: 'Show all asset types',
    tapSelectedAssetToChange: 'Tap the selected asset to change it.',
    noAssetsMatch: 'No assets match your search.',
    stepSelectChecklist: '2. Select checklist',
    noChecklistsForAssetType: 'No checklists match the asset type {{assetType}}.',
    selectAssetFirst: 'Select an asset first to show compatible checklists.',
    searchChecklistsPlaceholder: 'Search checklists',
    searchChecklistsA11y: 'Search checklists',
    showingChecklistsFor: 'Showing checklists for {{assetType}}',
    tapSelectedChecklistToChange: 'Tap the selected checklist to change it.',
    noChecklistsMatch: 'No checklists match your search.',
    stepAssignInspector: '3. Assign and start',
    youSuffix: ' (You)',
    you: 'You',
    onlyTeamMember: 'Only your team member is available right now.',
    createTeamToAssign: 'Create a team to assign inspections to others.',
    executionModeLabel: 'Execution mode',
    locationTitle: 'Current location',
    locationLoading: 'Checking your location...',
    locationNotSupported: 'Location is not supported on this device.',
    locationPermissionDenied: 'Location permission was denied.',
    locationUnavailableFallback: 'Location is unavailable.',
    locationUnavailableMessage: 'Location unavailable: {{reason}}',
    starting: 'Starting...',
    startInspection: 'Start inspection',
    prefilledMomentumHint: 'Review picks below, then tap Start inspection.',
    scheduleForLater: 'Schedule for later',
    noPriority: 'No priority',
    requestContextEyebrow: 'Inspection for request',
    requesterNotSet: 'Not set',
    requestHandoff: {
      requesterFallback: 'the requester',
      decisionStartNow: 'Decision: start now',
    },
    alerts: {
      selectAsset: 'Select an asset before continuing.',
      selectChecklist: 'Select a checklist before continuing.',
      selectAssetAndChecklist: 'Select an asset and a checklist to start.',
      assetArchivedTitle: 'Archived asset',
      assetArchivedBody: 'Archived assets cannot be inspected. Choose another asset.',
      assetArchivedScheduleBody: 'Archived assets cannot be scheduled for inspection. Choose another asset.',
      couldNotStartTitle: 'Could not start inspection',
      couldNotStartBody: 'Please try again.',
    },
  },
  share: {
    title: 'Share report',
  },
  // The shareable report document ("Ink & Amber" Phase 04). This is the surface
  // a customer hands to their own client, so the copy names the state plainly
  // and never sells.
  report: {
    coverEyebrow: 'Condition report',
    coverUntitled: 'Untitled asset',
    sectionSubject: 'Subject',
    statInspected: 'Inspected',
    statRepairs: 'Repairs',
    statEstTotal: 'Est. total',
  },
  sentForApproval: {
    subtitle:
      "We'll email a review link. You'll be notified when they respond.",
    backToHome: 'Back to home',
    viewReport: 'View report',
  },
  detail: {
    sendBack: {
      errors: {
        notAllowed: "Only the person who signs off this asset’s work can send it back.",
        notWaiting: "This inspection is no longer waiting for sign-off. Someone may have just approved it or sent it back.",
        ownWork: "You can’t send back an inspection you did yourself.",
        reasonRequired: "Write between 3 and 1,000 characters so they know what to fix.",
      },
      noticeHint: "Fix what they asked for, then tap Complete inspection again. It goes back to them to sign off.",
      noticeReason: "“{{reason}}”",
      noticeTitle: "{{name}} sent this back",
      approverFallback: "The approver",
      inspectorFallback: "the inspector",
      failedTitle: "Couldn’t send it back",
      sent: "Sent back to {{name}}. They’ll see your reason.",
      sending: "Sending back…",
      confirm: "Send back",
      reasonTooShort: "Write a few words so they know what to fix.",
      reasonPlaceholder: "For example: Add a photo of the roof drain.",
      reasonLabel: "What needs fixing",
      hint: "Say what to fix. {{name}} gets a notification and the inspection opens again for them.",
      title: "Send back to {{name}}",
      buttonA11y: "Send this inspection back to {{name}} with a reason",
      button: "Send back",
    },
    errors: {
      completeTitle: "Couldn’t complete the inspection",
      deleteTitle: "Couldn’t delete the inspection",
      continueTitle: "Couldn’t continue the inspection",
      takeOverTitle: "Couldn’t finish this for them",
      reopenTitle: "Couldn’t re-open the inspection",
      approveTitle: "Couldn’t approve the inspection",
      suggestionTitle: "Couldn’t add that suggestion",
      reloadTitle: "Couldn’t load their version",
      mergeTitle: "Couldn’t keep your answers",
      checkConnection: "Check your connection and try again.",
    },
    outline: {
      title: 'Items · {{done}} of {{total}} answered',
      jumpA11y: 'Go to item {{n}}, {{label}}, {{state}}',
      state: { failed: 'failed', done: 'answered', skipped: 'skipped', pending: 'not yet answered' },
    },
    findingsNextActionsTitle: 'Findings & next actions',
    assetContext: {
      asset: 'Asset',
      previousOnThisAsset: 'Previous on this asset',
      manuals: 'Manuals',
      noAssetLinked: 'No asset linked',
      openAsset: 'Open asset →',
      openAssetA11y: 'Open asset',
    },
    approval: {
      fixAndResend: "Fix and resend",
      fixHint: "Fix what they asked for, then send the report again.",
      changesAskedNoReason: "{{name}} asked for changes.",
      changesAsked: "{{name}} asked for changes: “{{reason}}”",
      linkCopied: "Link copied. Paste it into a message to {{name}}.",
      sendAgainA11y: "Send the report to {{name}} again",
      sendAgain: "Send again",
      sentToDue: "Sent to {{name}} on {{date}} · reply due {{due}}",
      sentTo: "Sent to {{name}} on {{date}}",
      summary: {
        nextNotShared: "Complete the inspection, then share the report if someone needs to approve it.",
        nextWaitingFor: "Waiting for {{name}} to sign off this inspection.",
        nextWaitingForTeam: "Waiting for someone in {{name}} to sign off this inspection.",
        nextStepWaited_one: "{{nextStep}} Waiting {{count}} day.",
        nextStepWaited_other: "{{nextStep}} Waiting {{count}} days.",
        nextYouApprove: "Check the findings, then choose Sign off.",
        nextWaitingShared: "Waiting for the person you shared it with to reply.",
        nextResponded: "Read the reply and decide what to do next.",
        nextRespondedJobs: "Approved repairs are ready to become jobs.",
        nextChangesRequested: "Read the feedback, update the inspection, then share it again.",
        nextApproved: "Signed off. Open Next steps or share the report.",
        nextApprovedJobs: "Check the approved repairs, then create jobs for them.",
        noApproverYet: "No approver yet",
        recipientFallback: "Recipient",
        statusNotShared: "Not shared",
        statusAwaiting: "Awaiting sign-off",
        statusResponded: "Replied",
        statusChangesRequested: "Changes requested",
        statusApproved: "Signed off",
      },
      askAiDraft: "Help me review the {{checklist}} inspection on {{asset}} from {{date}} and decide the next step.",
      closeIt: "Close it",
      keepWaiting: "Keep waiting",
      closeConfirmBody: "{{name}} hasn’t replied. The suggested repairs will be recorded as not approved, for them.",
      closeConfirmTitle: "Close without a reply?",
      takeOverSignedOff: "Approved in place of {{name}}",
      takeOverClosed: "Closed without a reply",
      takeOverApproved: "Approved for {{name}}",
      // Who can approve, not "{{name}} team": a team called "Team A" read "Team A team".
      teamApprover: "Anyone in {{name}}",
      assetApprover: "Asset approver",
      approverFallback: "the approver",
      reopenConfirm: "Re-open",
      reopenConfirmBody: "You can change the answers again, then complete the inspection again.",
      reopenConfirmApproved: "{{name}} approved it. Re-opening clears that approval, so it will need approving again.",
      reopenConfirmTitle: "Re-open this inspection?",
      approval: 'Approval',
      statusSummaryA11y: 'Approval status: {{status}}. {{action}}',
      collapse: 'Collapse',
      showDetails: 'Show details',
      currentStatus: 'Current status',
      approver: 'Approver',
      nextStep: 'Next step',
      approveInspectionA11y: 'Sign off this inspection',
      approving: 'Signing off…',
      approveInspection: 'Sign off',
      openNextStepsA11y: 'Open next steps',
      nextSteps: 'Next steps',
      shareReportA11y: 'Share the report',
      shareReport: 'Share report',
      reopenAndClearA11y: 'Re-open this inspection and clear its approval',
      reopenA11y: 'Re-open this inspection',
      reopening: 'Re-opening…',
      reopenAndClear: 'Re-open and clear approval',
      reopenInspection: 'Re-open inspection',
    },
    lifecycle: {
      pauseTitle: 'Pause Inspection',
      pauseHint: 'Optionally add a reason and schedule when to resume.',
      reasonOptional: 'Reason (optional)',
      pauseReasonPlaceholder: 'e.g. Waiting for parts, need customer approval',
      resumeDateOptional: 'Resume date (optional)',
      selectDate: 'Select date',
      resumeTimeOptional: 'Resume time (optional)',
      selectTime24h: 'Select time (24h)',
      cancel: 'Cancel',
      pause: 'Pause',
      cancelTitle: 'Cancel Inspection',
      cancelHint:
        'Progress is saved but the inspection will be marked cancelled. Please provide a reason.',
      cancellationReason: 'Cancellation reason',
      cancelReasonPlaceholder: 'e.g. Asset sold, duplicate inspection, wrong checklist',
      back: "Keep inspection",
      cancelInspection: 'Cancel inspection',
      reassignTitle: 'Reassign inspection',
      reassigned: 'Reassigned to {{name}}. The inspection stays in progress for them.',
      reassignFailed: "Couldn't reassign the inspection. Try again.",
      pickContractorTitle: "Give it to a contractor",
      contractorNoAccountTitle: "No AssetWRX account yet",
      contractorNoAccountBody: "{{name}} needs an AssetWRX account to run an inspection. Invite them from Contacts; once they join you can give it to them.",
      searchTeamMembersPlaceholder: 'Search team members…',
      noTeamMemberMatch: 'No team member matches.',
      cancelReasonRequired: 'Please provide a cancellation reason.',
      cancelledTitle: 'Cancelled',
      cancelledBody: 'Inspection has been cancelled.',
      cancelFailed: "Couldn't cancel the inspection. Try again.",
      pauseFailed: "Couldn't pause the inspection. Try again.",
      errorTitle: 'Error',
      skipTitle: 'Skip this item?',
      skipHint: 'Add a short note if it helps your team (optional). Tap Skip to confirm.',
      noteOptional: 'Note (optional)',
      skipReasonPlaceholder: 'e.g. No access, not applicable',
      skip: 'Skip',
    },
    repair: {
      proposedFollowUp: 'Needs a job?',
      confirmedAfterCompletion: '(confirmed after completion)',
      markForFollowUp: 'Yes, create a job for this',
      titleField: 'Title *',
      titlePlaceholder: 'e.g. Fix leaking faucet',
      priority: 'Priority',
      priorityFallback: 'Medium',
      description: 'Description *',
      descriptionPlaceholder: 'What needs to be done...',
      descriptionFallback: 'None',
      hideOptionsA11y: 'Hide follow-up options',
      showOptionsA11y: 'Show more follow-up options',
      lessOptions: 'Less options',
      moreOptions: 'More options',
      repairCategory: 'Repair category',
      repairCategoryFallback: 'Repair',
      skillsOptional: 'Skills (optional)',
      skills: 'Skills',
      selected: 'Selected',
      none: 'None',
      dueDateOptional: 'Due date (optional)',
      visibility: 'Visibility',
      visibilityFallback: 'Private',
      estimatedCostOptional: 'Estimated cost (optional)',
      notSet: 'Not set',
      costPlaceholder: '0.00',
      requiresUrgentRepair: 'Requires urgent repair',
      urgentRepairNoteLabel: 'Urgent repair note',
      noNote: 'No note',
      markFollowUpHintPrefix: "After you complete the inspection, you’ll confirm this job in ",
      markFollowUpHintSuffix: " before it’s created.",
    },
    urgentNote: {
      placeholder: 'Why urgent?',
      stop: 'Stop',
      dictate: 'Dictate',
      cleaningUpEllipsis: 'Cleaning up...',
      aiCleanUp: 'AI clean up',
      addNoteFirstTitle: 'Add a note first',
      addNoteFirstBody: 'Type a short note before using AI clean up.',
      cleaningUp: 'Cleaning up urgent note',
      unableToCleanUpTitle: 'Unable to clean up note',
      cleanedUpTitle: 'Cleaned-up urgent note',
      reviewSuggestedWording: 'Review the suggested wording before applying it.',
    },
    followUpModal: {
      eyebrow: 'Inspection action',
      title: 'Needs a job?',
      closeA11y: 'Close follow-up',
      saveHint: "Tick it, then complete the inspection. You’ll confirm the job before it’s created.",
      doneEditingA11y: 'Done editing proposed follow-up',
      done: 'Done',
    },
    imageFindingsOverlay: {
      findingA11y: 'Finding: {{label}}',
      confidenceSeverity: 'Confidence: {{confidence}}% · Severity: {{severity}}',
      removeMarkerA11y: 'Remove finding marker',
      removeMarker: 'Remove marker',
      close: 'Close',
    },
    quickRating: {
      quickRatingA11y: 'Quick rating',
      yes: 'Yes',
      no: 'No',
      quickLevelA11y: 'Quick level',
      conditionRatingA11y: 'Condition rating',
    },
    imageViewer: {
      couldNotLoad: 'Could not load image',
      closeA11y: 'Close image viewer',
    },
    noteSheet: {
      eyebrow: 'Inspection note',
      title: 'Add a note',
      closeA11y: 'Close note',
      hint: 'Type it, or dictate and it lands in the same note.',
      placeholder: 'Add inspection notes...',
      dictateStop: 'Stop dictating',
      dictateFinishing: 'Finishing…',
      dictateUnavailable: 'Dictation unavailable',
      dictateIdle: 'Dictate',
      doneA11y: 'Done',
      done: 'Done',
    },
    imageAnalysis: {
      coverage: {
        observed: "In view",
        partial: "Partly in view",
        not_observed: "Not in view: answer by hand",
      },
      analysePhoto: 'Analyse photo with AI',
      analysePhotoA11y: 'Analyse photo with AI',
      aiQueued: 'AI analysis queued',
      photoAnalysisTitle: 'Photo analysis',
      analysing: 'Analysing...',
      suggestedComment: 'Suggested comment',
      severity: 'Severity: {{severity}}',
      status: 'Status: {{status}}',
      findings: 'Findings',
      appendToComments: 'Append to comments',
      replaceComments: 'Replace comments',
      ignore: 'Ignore',
      removeFindingA11y: 'Remove finding {{label}}',
      remove: 'Remove',
      replaceConfirmTitle: 'Replace comments?',
      replaceConfirmBody: 'This will replace your existing note with the AI suggestion.',
      replaceConfirmCancel: 'Cancel',
      replaceConfirmReplace: 'Replace',
    },
    runnerHeader: {
      doneCount: '{{completed}}/{{total}} done',
      failCount: '{{count}} to follow up',
      urgentCountLabel_one: '{{count}} urgent',
      urgentCountLabel_other: '{{count}} urgent',
      modeLabel: {
        list: 'List',
        guidedGroup: 'By group',
        guidedItem: 'By item',
      },
      modeDesc: {
        list: 'See every item at once',
        guidedGroup: 'One group at a time',
        guidedItem: 'One item at a time',
      },
      cancelled: 'Cancelled',
      paused: 'Paused',
      sectionEyebrow: 'Section {{index}} of {{count}}',
      openAssetA11y: 'Open {{assetName}}',
      openSyncConflictA11y: 'Open sync conflict options',
      retrySaveA11y: 'Retry saving inspection answers',
      review: 'Review',
      retry: 'Retry',
      askAiCompactA11y: 'Based on your answers, AI can help with the next checks',
      askAiWideA11y: 'AI can help with the next checks',
      jumpToIncompleteA11y: 'Jump to next incomplete required item',
      jumpToIncomplete: 'Jump to incomplete',
      changeModeA11y: 'Change view mode. Current: {{mode}}',
      viewAsTitle: 'View this inspection as',
      modeOptionA11y: '{{mode}}: {{desc}}',
      viewModeAction: 'View: {{mode}}',
      viewModeTooltip: 'Change how the checklist is presented',
    },
  },
  overview: {
    statusApproved: 'Approved',
    statusCompleted: 'Completed',
    statusCancelled: 'Cancelled',
    statusPaused: 'Paused',
    statusInProgress: 'In Progress',
    items: 'Items',
    progress: 'Progress',
    failures: 'Failures',
    urgent: 'Urgent',
    resumeLabel: 'Resume: {{text}}',
    operationalTimelineA11y_one: 'Operational timeline, {{count}} event',
    operationalTimelineA11y_other: 'Operational timeline, {{count}} events',
    operationalTimelineTitle: 'Operational timeline · {{count}}',
    whoLabel: 'Who: {{actor}}',
    whenLabel: 'When: {{when}}',
  },
  photoEvidence: {
    photos: 'Photos',
    mediaGuidance: 'Up to 3 photos per item · draw to highlight before saving.',
    noPhotosTaken: 'No photos taken',
    evidencePhotoA11y: '{{label}} — evidence photo {{index}} of {{count}}. View full size',
  },
  shareSection: {
    statusChanges: "Changes asked",
    statusApproved: "Approved",
    statusWaiting: "Waiting",
    messageA11y: "Open review with {{name}}",
    reportSharedWith: 'Report shared with',
    message: 'Message',
    createJobs: 'Create jobs',
    revoke: 'Revoke',
    revokeAccessA11y: 'Revoke report access for {{name}}',
    revokeConfirmTitle: 'Revoke report access?',
    revokeConfirmBody: '{{name}} will no longer be able to open the report link. The share history is kept.',
    revokeConfirmCancel: 'Cancel',
    revokeConfirmConfirm: 'Revoke access',
    revokeFailedTitle: 'Could not revoke access',
    revokeFailedBody: 'Please try again in a moment.',
    recipientFallback: 'This recipient',
  },
  checklistSection: {
    batchHintRange: 'Items {{from}}–{{to}} of {{total}}',
    prefillNotice:
      'Previous inspection{{dateSuffix}}. Last time’s results and notes are shown for reference. Record the current result for each check.',
    prefillNoticeDateSuffix: ' on {{date}}',
    emptyGroup: 'Nothing to complete in this group.',
  },
  evidenceRail: {
    collapseA11y: 'Collapse {{title}}',
    hide: 'Hide',
  },
  swipeableWrap: {
    ok: 'OK',
    skip: 'Skip',
  },
  kpis: {
    items: 'Items',
    status: 'Status',
    cost: 'Est. cost',
    reviews: 'Reviews',
    percentDone: '{{percent}}% done',
    statusInProgress: 'In progress',
  },
  summaryCard: {
    suggestedTitle: "Suggested summary",
    title: 'Inspection summary',
    editSummary: 'Edit summary',
    noSummaryYet: 'No summary yet',
    generatedAt: 'Generated {{date}}',
    regenerate: 'Regenerate',
    generate: 'Generate',
    generating: 'Generating...',
    buildingSummary: 'Building summary from the inspection data...',
    noCredits: 'No usage credits left, so a summary cannot be written. Buy a credit pack, or wait for your monthly included credits.',
    noCreditsMember: 'No usage credits left, so a summary cannot be written. Ask a workspace admin to buy credits, or wait for your monthly included credits.',
    noCreditsOnDevice: 'No usage credits left, so a summary cannot be written. Credits are bought from AssetWRX on the web, or arrive with your monthly included credits.',
    topUp: 'Buy credits',
  },
  valueAnswer: {
    readingLabel: "Reading",
    textLabel: "Answer",
    textPlaceholder: "Type or scan",
    scan: "Scan",
    detected: "Detected: {{value}}",
    detectedOnDevice: "Read on this device, no AI credits used",
    use: "Use",
    ignore: "Ignore",
  },
  checklistEditor: {
    required: "Required",
    yes: "Yes",
    no: "No",
    ratingReading: "Reading",
    ratingText: "Text",
    meterLabel: "Meter",
    meterHintMissing: "Choose which meter this is. Only then is the reading added to the asset’s meter history when the inspection is completed.",
    meterHintUnit: "Add the unit (for example bar or °C) so the reading can be added to the asset’s meter history.",
    meterHintSaved: "Added to the asset’s {{meter}} readings when the inspection is completed, and counts towards meter-based maintenance.",
    meterHours: "Hours",
    meterDistance: "Distance",
    meterCycles: "Cycles",
    meterOther: "Other",
    unitLabel: "Unit",
    visionCanAssist: "Vision can answer this from a photo",
    useVision: "Scan this section with Vision",
    visionOff: "Off",
    visionAvailable: "Available",
    visionRecommended: "Recommended",
    evidenceAlways: "Photo evidence expected",
    evidenceOnFail: "Photo evidence expected when it fails",
    measureLabel: 'Measure',
    ratingCondition: 'Condition',
    ratingLevel: 'Level',
    ratingYesNo: 'Yes/No',
    ratingPassFailNa: 'Pass/Fail/N/A',
    ratingPassFlagFail: 'Pass/Flag/Fail',
    failWhenLabel: 'Fail when',
    yesNoFailHint: 'Use Yes for issue-present prompts (e.g. "Any cracks?"). Use No for compliance prompts (e.g. "Is guard fitted?").',
    captureLabel: 'Capture',
    images: 'Images',
    videos: 'Videos',
    groupNameA11y: 'Group name',
    groupNamePlaceholder: 'Group name',
    itemLabelA11yIndexed: 'Item {{index}} label',
    itemLabelPlaceholder: 'Item label',
    itemDescriptionA11yIndexed: 'Item {{index}} description (optional)',
    descriptionOptionalPlaceholder: 'Description (optional)',
    addItemToGroup: 'Add item to group',
    moveUpA11y: "Move up",
    moveDownA11y: "Move down",
    removeGroupA11y: "Remove group",
    removeItemA11y: "Remove item",
    dragA11y: "Reorder: press and hold, then drag",
    itemLabelA11y: 'Item label',
    itemLabelPlaceholderExample: 'Item label (e.g. Check engine oil)',
    itemDescriptionA11y: 'Item description (optional)',
    requiredNeedsOneItem: 'A checklist needs at least one item.',
    groupDefaultLabel: 'Group {{number}}',
    inspectionNotFoundTitle: 'Inspection not found',
    couldNotLoadItemsBody: 'Could not load this inspection to copy items.',
    noItemsToCopyTitle: 'No items to copy',
    noItemsToCopyBody: 'This inspection has no checklist items to use as a checklist.',
    couldNotLoadInspectionTitle: 'Could not load inspection',
    tryAgainOrCreateManually: 'Try again or create the checklist manually.',
    workflowCopySuffix: '{{name}} (workflow copy)',
    unsavedTemplateMessage: 'This checklist hasn’t been saved yet. Leave and lose it?',
    unsavedEditsMessage: "You have unsaved edits to this checklist. Leave and lose them?",
    createFailed: "Failed to create checklist.",
    updateFailed: "Failed to update checklist.",
    unsavedTemplateEditMessage: 'This checklist has unsaved changes. Leave and lose them?',
    templateNameLabel: 'Checklist name *',
    templateNameA11y: 'Checklist name (required)',
    templateNamePlaceholder: 'e.g. Pre-Purchase Car Inspection',
    generateDraft: 'Generate draft',
    templateDetailsTitle: 'Checklist details and visibility',
    templateDetailsSummary: 'Asset compatibility, description, categories, and team visibility.',
    assetTypeCompatibility: 'Asset type compatibility',
    assetTypeHint: 'Select one asset type this checklist applies to. Leave empty for any type.',
    customTypesLabel: 'Your asset types',
    customTypesHint: 'Pick types to use this checklist only for them. Leave empty for every asset of the kind above.',
    placeTypesLabel: 'Kinds of place',
    placeTypesHint: 'Pick kinds of place, such as Room, to use this checklist only there. Leave empty for every place.',
    any: 'Any',
    visibilityLabel: 'Visibility',
    visibilityAllTeams: 'All teams in account can use this checklist',
    visibilityOnlySelectedTeams: 'Only selected teams (default: your current team)',
    visibilityCreateTeamHint: 'Create a team in Account to restrict visibility',
    descriptionHint: 'Short summary when selecting this checklist. Optional.',
    descriptionPlaceholderExample: 'e.g. Pre-purchase vehicle check',
    categoriesLabel: 'Categories',
    categoriesHint: 'Select one or more categories',
    checklistItemsTitle: 'Checklist items',
    addGroup: 'Add group',
    addItem: 'Add item',
    saveChecklist: 'Save checklist',
    templateStructureTitle: 'Checklist structure',
    evidenceCoverageTitle: 'Evidence coverage',
    evidenceCoverageCopy_one: '{{count}} item currently captures at least one evidence type.',
    evidenceCoverageCopy_other: '{{count}} items currently capture at least one evidence type.',
    versioningTitle: 'Versioning',
    versioningCopy: "Your changes are saved as a new version. Inspections already started keep the checklist they began with.",
    newTemplateTitle: 'New checklist',
    templateNotFound: 'Checklist not found',
    loadingTemplate: 'Loading checklist…',
    startTemplateTitle: 'Start your checklist',
    startTemplateBody: 'Add the first item or group your inspector should run through. Or use AI to generate a draft.',
    addFirstItem: 'Add your first item',
    addAGroup: 'Add a group',
    validationTitle: 'Validation',
    failedToCreateChecklist: 'Failed to create checklist.',
    failedToUpdateChecklist: 'Failed to update checklist.',
    editTemplateTitle: 'Edit checklist',
    saveChanges: 'Save Changes',
  },
  checklistDetail: {
    loadErrorTitle: 'Couldn’t load this checklist',
    categoryGeneral: 'General',
    versionLabel: 'Version v{{version}}',
    updatedDate: 'Updated {{date}}',
    currentVersionIdLabel: 'Current version ID:',
    versionIdNotSet: 'Not set',
    assetTypesLabel: 'Asset types:',
    allAssetTypesShort: 'All',
    scopeLabel: 'Scope:',
    // Rows aligned with Asset types / Scope / Structure (they ran together on one wrapping line).
    versionRowLabel: 'Version:',
    versionRowValue: 'v{{version}}',
    createdRowLabel: 'Created:',
    updatedRowLabel: 'Updated:',
    selectedTeamsOnly: 'Selected teams only',
    structureLabel: 'Structure:',
    groupsCount_one: '{{count}} group',
    groupsCount_other: '{{count}} groups',
    itemsCount_one: '{{count}} item',
    itemsCount_other: '{{count}} items',
    requiredCountSuffix_one: ', {{count}} required',
    requiredCountSuffix_other: ', {{count}} required',
    useTemplateForInspectionA11y: 'Use checklist for inspection',
    itemsCountTitle: 'Items · {{count}}',
    required: 'Required',
    ungrouped: 'Ungrouped',
  },
  suggestScreen: {
    partialCreatedBody_other: "{{count}} of {{total}} jobs created. Couldn’t create “{{title}}”. Try again.",
    partialCreatedBody_one: "{{count}} of {{total}} jobs created. Couldn’t create “{{title}}”. Try again.",
    linkFailed_other: "{{count}} jobs created, but we couldn’t add them to the request. Try again.",
    linkFailed_one: "Job created, but we couldn’t add it to the request. Try again.",
    missingInspectionBody: "Go back and open the inspection again.",
    partialCreatedTitle: "Some jobs weren’t created",
    suggestedJobA11y: 'Suggested job {{title}}',
    findingsCount_one: '{{count}} finding',
    findingsCount_other: '{{count}} findings',
    missingTitleOrCategory: 'Missing: Title or Category',
    editSuggestedJobTitle: 'Edit suggested job',
    titlePlaceholderRepairPipe: 'e.g. Repair leaking pipe',
    scopeOfWorkPlaceholder: 'Scope of work',
    whereJobCarriedOutPlaceholder: 'Where will this job be carried out?',
    assignToRecommendedA11y: 'Assign to {{name}}',
    suggestedAssigneeLabel: 'Suggested: {{name}} · {{rationale}}',
    workPathLabel: 'Work path',
    missingInspection: 'Missing inspection',
    loadingEllipsis: 'Loading…',
    accessRestrictedTitle: 'Access restricted',
    accessRestrictedBody: "Only the inspection creator or a member of the asset's team can create jobs from these failed items.",
    accessDeniedAlertTitle: 'Access denied',
    accessDeniedAlertBody: 'Only the inspection creator or asset team can create these jobs.',
    selectSuggestedJobsTitle: 'Select suggested jobs',
    selectSuggestedJobsBody: 'Select at least one suggestion to create.',
    missingDetailsBodySuggest: 'Some selected suggestions are missing Title or Category. Edit those cards before creating jobs.',
    jobsAlreadyCreated: 'Jobs already created',
    noSuggestedJobs: 'No suggested jobs',
    allFindingsLinkedBody: 'Every failed or repair-marked finding on this inspection already has a linked job.',
    noFailedItemsBody: 'This inspection does not currently contain failed or repair-marked items that need a follow-up job.',
    openReport: 'Open report',
    linkedJobsTitle: 'Linked jobs',
    linkedJobsHintCannotRecreate: 'These findings are already linked to jobs and cannot be created again unless the job is deleted.',
    assetFallback: 'Asset',
    suggestionsCountPill_one: '{{count}} suggestion',
    suggestionsCountPill_other: '{{count}} suggestions',
    fromFailedItemsPill_one: 'from {{count}} failed item',
    fromFailedItemsPill_other: 'from {{count}} failed items',
    alreadyCreatedTitle: 'Already created',
    linkedFindingsHint_one: '{{count}} finding already has linked jobs and will not be suggested again.',
    linkedFindingsHint_other: '{{count}} findings already have linked jobs and will not be suggested again.',
    followUpJobsFromMarkedItems: "Jobs from failed items",
    aiDraftedHint: 'AI-drafted. Untick anything that shouldn’t become work.',
    ruleBasedHint: 'Untick anything that shouldn’t become work.',
    progressLinkNeedsAttention: "Not added to the request",
    progressLinkMessage: 'The job was created, but the shared request/report progress did not update. {{message}}',
    retryLink: "Try again",
    stillNotLinkedTitle: 'Still not linked',
    jobCreatedNotLinkedFallback: 'The job was created but could not be linked to the shared progress record.',
    unableToLinkJobFallback: 'Unable to link the job to the request.',
    missingDetailsBanner_one: '{{count}} selected suggestion needs a title or category before jobs can be created. Skills are recommended but optional for suggested jobs.',
    missingDetailsBanner_other: '{{count}} selected suggestions need a title or category before jobs can be created. Skills are recommended but optional for suggested jobs.',
    createJobsA11y_one: 'Create {{count}} suggested job',
    createJobsA11y_other: 'Create {{count}} suggested jobs',
    creatingEllipsisEn: 'Creating...',
    createJobsButton_one: 'Create {{count}} job',
    aiDraft: {
      button: "Draft with AI",
      again: "Draft again with AI",
      notEnough: "Not enough AI credits",
      failedTitle: "AI drafting did not work",
      failedBody: "Your list is unchanged. Please try again.",
    },
    createAndAssignButton_one: "Create and assign {{count}} job",
    createAndAssignButton_other: "Create and assign {{count}} jobs",
    approvedBy: "Approved by {{name}}",
    handOverIncompleteTitle: "Created, not all handed over",
    handOverIncompleteBody: "The jobs exist, but these were not handed over or the contractor was not told: {{names}}. Open each job to finish.",
    createJobsButton_other: 'Create {{count}} jobs',
  },
  shareScreen: {
    askApproveSuggested_other: "Also ask them to approve {{count}} suggested repairs",
    askApproveSuggested_one: "Also ask them to approve {{count}} suggested repair",
    askApproveSuggestedHint: "They can approve or decline each one.",
    relationshipChangeA11y: "Change their relationship to the asset",
    relationshipChange: "Change",
    relationship: "Their relationship: {{role}}",
    greetingTemplateRepairs: "Hi,\n\nHere is the inspection report for {{assetName}}. Open the link to read it and tap Decline on any suggested repair you do not want. Then sign to sign off, or tap Send back to ask for changes.\n\nThank you.",
    greetingTemplate: "Hi,\n\nHere is the inspection report for {{assetName}}. Open the link to read it, then sign to sign off, or tap Send back to ask for changes.\n\nThank you.",
    assetFallback: 'your asset',
    emailNotSent: 'Shared, but the email did not send. Copy the link to send it yourself.',
    copyLink: 'Copy link',
    shareCreatedTitle: 'Share created',
    shareThisLinkBody: 'Share this link:\n{{link}}',
    inspectionNotFound: 'Inspection not found.',
    contactNeedsEmail: "Add their email address to send the report by email.",
    checkEmailAddress: "Check the email address: it does not look like a valid address.",
    checkPhoneNumber: 'Check the phone number: enter a valid number for the selected country, or leave it empty.',
    reportResentNotify: "Report sent again. You're notified when they reply.",
    reportSharedNotify: "Report sent. They get an email with a link, and you're notified when they reply.",
    createShareFailedFallback: 'Failed to create share.',
    errorTitle: 'Error',
    requiredTitle: 'Required',
    enterEmailRequired: 'Please enter an email address (or select a contact with an email).',
    recipientFallback: 'Recipient',
    contactFallback: 'Contact',
    selectContactTitle: 'Select a contact',
    selectContactBody: 'Sharing is contact-based. Please tap "Select contact" and choose or add a contact to share with.',
    respondByLine: 'Please respond by {{date}}\n\n',
    estimatedTotalLine: 'Estimated total: {{amount}}\n\n',
    reviewLinkLine: 'Review Link: {{link}}',
    assetFallbackShort: 'Asset',
    emailSubject: "Inspection report – {{assetName}}",
    resentTitle: 'Resent',
    sentTitle: 'Sent',
    resentBody: 'Inspection report resent to recipient.',
    sentBody: 'Inspection report shared. Recipient will be notified.',
    deliveryUnconfirmed: 'Share was created but delivery could not be confirmed. Share this link manually:\n{{link}}',
    inspectionNotFoundPlain: 'Inspection not found',
    onlyCreatorAssigneeOrTeamCanShare:
      "Only this inspection's creator, its assigned inspector, or a member of the asset's team can share the report.",
    shareLinkNotConfiguredProd:
      "A shareable web link isn't set up for this workspace yet, so recipients may not be able to open the report link. Please contact your administrator.",
    reviewAssistantTitle: 'Review assistant',
    sending: 'Sending…',
    resendReportTo: 'Resend report to {{name}}',
    sendReportTo: 'Send report to {{name}}',
    theRequesterFallback: 'the requester',
    oneTapHint: "Filled in from the request. Change the person or message below if you need to.",
    shareTo: "Send to",
    contactsHint: "Tap Select contact and pick the person. People you sent this to before are at the top.",
    whoCanOpen: "Only they can open the report. The link in their email signs them straight in. Anyone else who gets the link can only ask you for access.",
    changeContact: 'Change contact',
    selectContact: 'Select contact',
    primaryChooseRole: 'Choose role',
    primaryAddEmail: 'Add email',
    primaryResend: "Send again",
    primarySend: "Send report",
    noEmailOrPhone: 'No email or phone',
    shareToContactTitle: "Send to",
    howToSend: 'How to send',
    byEmail: 'By email',
    roleLabel: 'Role *',
    recipientNameOptional: "Their name (optional)",
    recipientNamePlaceholder: 'e.g. John Smith',
    recipientEmailLabel: "Their email *",
    recipientEmailA11y: "Their email (required)",
    recipientEmailPlaceholder: 'client@example.com',
    phoneOptional: 'Phone (optional)',
    shareStatus: "Already sent",
    firstSharedLabel: "Already sent on {{date}}",
    lastResharedLabel: "Sent again on {{date}}",
    noResponseByDeadline: "No reply by the date you set. Tap Send again to remind them.",
    responseDeadlineOptional: "Reply by (optional)",
    messageOptional: 'Message (optional)',
    messagePlaceholder: 'Add a note for the recipient...',
    includeSuggestedJobsTitle: 'Include suggested jobs in shared review',
    includeSuggestedJobsHint_one: 'Adds {{count}} suggested follow-up item for recipient approval.',
    includeSuggestedJobsHint_other: 'Adds {{count}} suggested follow-up items for recipient approval.',
    missingEstimateWarning_one:
      "{{count}} item has no cost estimate — it'll still be in the report, just without a cost. You can share now or add estimates first.",
    missingEstimateWarning_other:
      "{{count}} items have no cost estimate — they'll still be in the report, just without a cost. You can share now or add estimates first.",
    couldNotShareReportTitle: 'Could not share report',
    sharingFailedFallback: 'Sharing failed. Check your connection and try again.',
  },
  reportScreen: {
    exportFailed: {
      word: "Couldn’t create the Word file. Try again.",
      excel: "Couldn’t create the Excel file. Try again.",
      pdf: "Couldn’t create the PDF. Try again.",
    },
    exportPreparing: {
      word: "Preparing the Word file…",
      excel: "Preparing the Excel file…",
      pdf: "Preparing the PDF…",
    },
    editRepairDetailsTitle: 'Edit repair details',
    saveFailedFallback: 'Failed to save.',
    titleLabel: 'Title',
    titlePlaceholder: 'e.g. Fix leaking faucet',
    categoryLabel: 'Category',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'What needs to be done...',
    priorityLabel: 'Priority',
    dueDateOptional: 'Due date (optional)',
    estimatedCostOptional: 'Estimated cost (optional)',
    estimatedCostA11y: 'Estimated cost amount',
    visibilityLabel: 'Visibility',
    saveRepairFailedTitle: 'Could not save repair',
    syncFailedGeneric: 'Sync failed.',
    skipped: 'Skipped',
    downloadPdf: 'Download PDF',
    downloadExcel: 'Download Excel',
    downloadWord: 'Download Word',
    sharePdfToGoogleDrive: 'Share PDF to Google Drive…',
    shareExcelToOneDrive: 'Share Excel to OneDrive…',
    shareWordToSharePoint: 'Share Word to SharePoint…',
    googleDrive: 'Google Drive',
    oneDrive: 'OneDrive',
    sharePoint: 'SharePoint',
    shareRequiredTitle: "Share the report first",
    shareRequiredBody: "Tap Share and choose who it is for. That makes the link they open to read and answer it.",
    hostedLinkCopiedTitle: "Review link copied",
    inspectionReportFileLabel: 'Inspection report ({{format}})',
    exportFileError: 'Could not generate export file.',
    exportReadyTitle: 'Export ready',
    exportReadyBody: 'Your file download has started.',
    reportInsightTitle: 'Report insight',
    reviewRepairEvidenceAction: 'Review repair evidence and estimated cost before export.',
    shareReportAction: "Tap Share to send a link people can open to read and answer the report.",
    runFirstExportAction: "Tap Download to save a PDF, Excel or Word copy with your logo.",
    itemsVisible_one: '{{count}} inspection item is visible in the report.',
    itemsVisible_other: '{{count}} inspection items are visible in the report.',
    repairFindingsIncluded_one: '{{count}} repair finding is included.',
    repairFindingsIncluded_other: '{{count}} repair findings are included.',
    noRepairFindingsIncluded: 'No repair findings are included.',
    hostedLinkAvailable: "A review link is ready to send.",
    hostedLinkNotAvailable: "No review link yet. Tap Share to make one.",
    exportRecords_one: '{{count}} recent export record exists.',
    exportRecords_other: '{{count}} recent export records exist.',
    noExportActivity: "Nobody has downloaded this report yet.",
    metricInspected: 'Inspected',
    metricRepairs: 'Repairs',
    metricExports: 'Exports',
    metricSummary: 'Summary',
    summaryReady: 'Ready',
    summaryMissing: 'Missing',
    insightSourceLabel: "this inspection's items and repairs",
    nextSteps: 'Next steps',
    copyHostedReportLink: "Copy review link",
    brandingSettings: 'Branding settings',
    moreReportOptionsA11y: 'More report options',
    yourSignOff: 'Your sign-off',
    signOff: 'Sign off',
    reviewAndSignOff: "Approve or send back",
    shareA11y: 'Share',
    share: 'Share',
    assignedTo: 'Assigned to {{name}}',
    unassigned: 'Unassigned',
    quoteAwarded: 'Quote awarded',
    quoteRequested: 'Quote requested',
    quotes: 'Quotes',
    jobsCreated_one: '{{count}} job created',
    jobsCreated_other: '{{count}} jobs created',
    findingsMayNeedJobs_one: '{{count}} finding may need jobs',
    findingsMayNeedJobs_other: '{{count}} findings may need jobs',
    noRepairFindingsInReport: 'No repair findings in this report',
    createJobsFromFindingsA11y: "Confirm jobs from the findings",
    createJobsFromFindings: "Confirm jobs",
    openLinkedJobsA11y: 'Open linked jobs',
    openLinkedJobs_one: 'Open linked job',
    openLinkedJobs_other: 'Open linked jobs',
    approved: 'Signed off',
    awaitingApproval: 'Waiting for sign-off',
    approvedBy: 'Signed off by {{name}}',
    recipientFallback: 'recipient',
    approvedOnAt: ' on {{date}} at {{time}}',
    reviewRequested: 'Review requested',
    sharedWithName: 'Shared with {{name}}',
    reviewRequestedFallback: 'review requested',
    firstShared: 'First shared {{date}}',
    lastReshared: ' | Last reshared {{date}}',
    resendAndUpdateDeadline: 'Resend & update deadline',
    noReviewRequestedYet: 'No review has been requested yet.',
    notSharedYetPrefix: 'Not shared yet — use ',
    shareAboveLink: 'Share above',
    notSharedYetSuffix: ' to request a review.',
    theInspectorFallback: 'the inspector',
    askInspectorEmptyHint: 'Ask the inspector about this report. No messages yet.',
    discussInspectionEmptyHint: 'Discuss this inspection with the reviewer. No messages yet.',
    atAGlance: 'At a glance',
    estTotal: 'Est. total',
    approvalLabel: 'Sign-off',
    approvalAwaiting: 'Awaiting',
    jobsLabel: 'Jobs',
    recentExports: 'Recent exports',
    loadingExportHistory: 'Loading export history...',
    noExportsRecorded: 'No exports recorded yet for this report.',
    loadErrorTitle: 'Couldn’t load this report',
    goBack: 'Go back',
    reportAvailableWhenCompleted: 'Report available when inspection is completed.',
    findingsAndEvidence: 'Findings & evidence',
    noItemsRecorded: 'No inspection items were recorded for this inspection.',
    otherGroup: 'Other',
    inspectorNotes: 'Inspector notes',
    noteLabel: 'Note',
    itemSkippedFallback: 'This item was skipped during inspection.',
    mediaCount: 'Media · {{count}}',
    playVideo: 'Play video {{index}}',
    repairAndRemediation: 'Repair & remediation',
    descriptionOrRecommendation: 'Description',
    recommendation: 'Recommendation',
    labourAndMaterials: 'Labour & materials',
    urgentFallback: 'Urgent',
    requiredSkills: 'Required skills',
    dueDate: 'Due date',
    estimatedCost: 'Estimated cost',
    urgentRepairNote: 'Urgent repair note',
    urgentRepairNoteFallback: 'This item has been flagged as requiring urgent attention.',
    jobCreated: 'Job created',
    jobsCreatedPlain: 'Jobs created',
    editRepairDetails: 'Edit repair details',
    whereThisStands: 'Where this stands',
    reportOptionsTitle: 'Report options',
    couldNotLoadImageSubtext: 'The image may have been moved or is unavailable.',
    syncReportWorkspaceFailedTitle: "Couldn’t create the file",
    statusPrefix: 'Status: {{status}}',
    templateVersionLabel: 'Checklist: {{name}}{{version}}',
    conductedInspection: 'Conducted inspection',
    inspectorFallback: 'Inspector',
  },
  convertScreen: {
    missingShareBody: "Open this from the inspection report to turn approved repairs into jobs.",
    missingShareTitle: "Nothing to open here",
    editA11y: 'Edit {{title}}',
    dueLabel: 'Due {{date}}',
    missingFieldsHint: 'Missing: Title, Category or Required skills',
    editJobDetailsTitle: 'Edit job details',
    requiredTitle: 'Required',
    requiredBody: 'Title and Category are required.',
    titleLabel: 'Title',
    titlePlaceholder: 'e.g. Fix leaking faucet',
    categoryLabel: 'Category *',
    priorityLabel: 'Priority (optional)',
    dueDateLabel: 'Due date (optional)',
    jobLocationLabel: 'Job location (optional)',
    addressSearchPlaceholder: 'Search address…',
    estimateLabel: 'Estimate (optional)',
    costPlaceholder: '0.00',
    assigneeLabel: 'Assignee (optional)',
    noneOption: 'None',
    saving: 'Saving…',
    save: 'Save',
    missingShare: 'Missing share',
    loading: 'Loading...',
    noApprovedRepairsTitle: 'No approved repairs',
    noApprovedRepairsBody: 'The customer has not approved any repairs for this share yet.',
    onlyCreatorOrTeamCanConvert:
      "Only the inspection creator or a member of the asset's team can create jobs from this share.",
    unsavedJobEditsMessage: 'You have unsaved job edits. Leave and lose them?',
    leave: 'Leave',
    keepEditing: 'Keep editing',
    createJobsErrorTitle: "Couldn’t create the jobs",
    createJobsErrorFallback: 'Failed to create jobs.',
    selectRepairsTitle: 'Select repairs',
    selectRepairsBody:
      'Select at least one approved repair (check the box on each card). Tap a card to edit title, category, or assignee before creating.',
    missingDetailsTitle: 'Missing details',
    missingDetailsBody:
      'Some selected repairs are missing Title, Category or Required skills. Tap each card to edit before creating jobs.',
    step1WorkPath: "Who will do the work?",
    step1WorkPathHint: "Choose your team, one contractor, or ask for quotes. You’ll pick the people after the jobs are created.",
    step2SelectItems: "Which repairs need a job?",
    creatingJobs: 'Creating…',
    createJobs_one: 'Create {{count}} job',
    createJobs_other: 'Create {{count}} jobs',
  },
  completeScreen: {
    approvedToast: "Inspection signed off. Share the report, or choose Done.",
    alerts: {
      optionalBody_other: "{{count}} optional items aren’t answered or skipped. Complete anyway?",
      optionalBody_one: "{{count}} optional item isn’t answered or skipped. Complete anyway?",
      failedTitle_other: "{{count}} failed items have no photo or note",
      failedTitle_one: "{{count}} failed item has no photo or note",
      requiredBody_other: "Answer or skip {{count}} required items before you complete this inspection.",
      requiredBody_one: "Answer or skip {{count}} required item before you complete this inspection.",
      completedToast: "Inspection completed",
      optionalTitle: "Some items not answered",
      goToFailed: "Go to failed item",
      failedBody: "Add a photo or note so others can see the problem, or complete it as it is.",
      jumpToFirst: "Go to the first one",
      requiredTitle: "Required items not done",
      tryAgain: "Try again",
      savingBody: "They’re kept on this device. Tap Try again, then complete the inspection.",
      savingTitle: "Some answers haven’t saved yet",
      conflictChoose: "Choose",
      conflictBody: "Choose Keep my answers or Use theirs, then complete it.",
      conflictTitle: "Someone else changed this inspection",
    },
    status: {
      nextVisit: "Next visit due {{date}}.",
      requesterWaiting: "The requester is waiting for this report.",
      sentTo: "Sent to {{name}} for approval.",
      approveSelf: "Check the findings, then choose Sign off.",
      approveOrLeave: "Sign it off yourself, or leave it for {{name}}.",
    },
    actions: {
      approve: "Sign off",
      createCertificate: "Issue renewed certificate",
      shareRequester: "Share report with requester",
      createApprovedJobs: "Create approved jobs",
      scheduleNext: "Schedule next inspection",
      shareReport: "Share report",
      exportReport: "Export report",
      report: "Report",
      done: "Done",
      confirmJobs: "Confirm jobs",
    },
    loadErrorTitle: 'Couldn’t load this inspection',
    loadErrorDescription: 'Check your connection and try again.',
    notFoundTitle: 'Inspection not found',
    notFoundDescription: 'This inspection may have been removed, or the link is out of date.',
    goBack: 'Go back',
    notCompletedYet: "This inspection isn’t finished yet.",
    back: 'Back',
    generateSummaryError: 'Could not generate the inspection summary. Please try again.',
    saveSummaryError: 'Could not save the inspection summary. Please try again.',
    reopenErrorTitle: 'Could not re-open',
    reopenErrorFallback: 'Please try again.',
    jobFollowUpTitle: '{{name}} follow-up',
    jobFollowUpDescription: "Follow-up from the {{checklist}} inspection on {{assetName}}, {{date}}.",
    assetFallback: 'asset',
    templateFallback: 'Inspection',
    certificateTitle: '{{name}} certificate',
    cannotCreateCertificateTitle: 'Cannot create certificate',
    cannotCreateCertificateBody: 'This inspection is not linked to an asset.',
    certificateCreatedTitle: 'Certificate created',
    certificateCreatedBody: "Certificate saved to the asset’s Documents.",
    errorTitle: "Couldn’t create the certificate",
    certificateCreateError: 'Could not create certificate.',
    inspectedLabel: 'Inspected',
    failedItemsLabel: 'Failed items',
    followUpJobsLabel: "Suggested jobs",
    inspectionAssistantTitle: 'Inspection assistant',
    confirmNextActions: 'What happens next',
    moreCompletionActions: 'More',
    moreCompletionActionsSummary: "Create a job by hand, re-open the inspection, or share the report.",
    createJobNow: 'Create job now',
    notifyTeam: 'Notify team',
    syncFailedTitle: "Couldn’t save the summary",
    syncFailedFallback: 'Request failed. Check your connection and try again.',
  },
  templateCard: {
    allAssetTypes: 'All asset types',
    categoryGeneral: 'General',
    categoriesCount_one: '{{count}} category',
    categoriesCount_other: '{{count}} categories',
    usedCount_zero: 'Not used yet',
    usedCount_one: 'Used once',
    usedCount_other: 'Used {{count}} times',
    createdDate: 'Created {{date}}',
    allTeams: 'All teams',
    selectedTeams: 'Selected teams',
  },
  checklistCard: {
    allTypes: 'All types',
    shared: 'Shared',
    totalItems: 'Total Items',
    required: 'Required',
    optional: 'Optional',
    usedTimes_one: 'Used {{count}} time',
    usedTimes_other: 'Used {{count}} times',
    completedCount_one: '{{count}} completed',
    completedCount_other: '{{count}} completed',
    estMinutes: '~{{count}} min',
    createdDate: 'Created {{date}}',
  },
  conditionalEditor: {
    showOnlyWhen: 'Show only when',
    addPreviousItemFirst: 'Add a previous item first to make this conditional.',
    parentItem: 'Parent item',
    untitledItem: 'Untitled item',
    alwaysShown: 'Always shown',
    expectedAnswer: 'Expected answer',
    notConditional: 'Not conditional',
    clearCondition: 'Clear condition',
    onlyShownWhen: 'Only shown when "{{parent}}" is {{value}}.',
    conditionInvalidAfterReorder:
      'Current condition is no longer valid after reordering. Choose an earlier item or clear it.',
    itemAlwaysShown: 'This item is always shown.',
  },
  card: {
    awaitingReviewerResponse: 'Awaiting reviewer response',
    inspectorFallback: 'Inspector',
    recipientFallback: 'Recipient',
    sharePendingCount_one: '{{count}} pending',
    sharePendingCount_other: '{{count}} pending',
    shareDeclinedCount_one: '{{count}} declined',
    shareDeclinedCount_other: '{{count}} declined',
    shareApprovedCount_one: '{{count}} approved',
    shareApprovedCount_other: '{{count}} approved',
    sharedWith: 'Shared with {{names}}',
    responseDue: 'Response due {{date}}',
    templateFallback: 'Inspection',
    assetFallback: 'Unknown asset',
    inspectorA11y: 'Inspector {{name}}',
    issuesCountA11y_one: '{{count}} issue',
    issuesCountA11y_other: '{{count}} issues',
    repairsCountA11y_one: '{{count}} repair',
    repairsCountA11y_other: '{{count}} repairs',
    urgentCountA11y_one: '{{count}} urgent',
    urgentCountA11y_other: '{{count}} urgent',
    openAssetA11y: 'Open asset {{assetName}}',
    urgentPill: 'Urgent {{count}}',
    issuesPill: 'Issues {{count}}',
    repairsPill: 'Repairs {{count}}',
    awaitingApproval: 'Awaiting approval',
    sharedDense: 'Shared',
    reportShared: 'Report shared',
  },
  noteSuggestions: {
    suggestions: 'Suggestions',
    tapToInsert: 'Tap to insert',
    findingRecentNotes: 'Finding recent notes…',
  },
  /** The runner's item controls, footer and summary line (were English literals). */
  runner: {
    skipGroupBody_other: "Mark {{count}} items in this group as skipped, then go to the next group?",
    skipGroupBody_one: "Mark {{count}} item in this group as skipped, then go to the next group?",
    syncUnsavedBanner: "A change didn’t save. Tap to try again.",
    syncFailedBanner: "Some answers haven’t saved yet. They’re kept on this device. Tap to try again.",
    tryAgain: "Please try again.",
    moveToItemTitle: "Couldn’t jump to that item",
    captureUnsupported: "This kind of capture can’t be saved to an item.",
    captureScanBody: "QR codes are read by the scanner, not saved to items.",
    captureScanTitle: "Use the scanner for QR codes",
    captureMeasurementAdded: "Measurement added to the item’s note.",
    captureMeasurement: "Measurement: {{value}}",
    captureNoteAdded: "Note added to the item.",
    capturePhotoAdded: "Photo added to the item.",
    captureNoNote: "No note was captured.",
    captureNoPhoto: "No photo was taken.",
    capturePhotosOff: "This item doesn’t take photos.",
    captureNoItem: "Open an item first, then capture again.",
    captureMismatch: "This capture is for a different inspection.",
    captureNotFound: "That capture couldn’t be found. Capture it again.",
    captureNotSavedTitle: "Capture not saved",
    skipGroupReason: "Group not applicable",
    skipGroupConfirm: "Skip group",
    skipGroupTitle: "Skip this group?",
    keysClose: "Close this panel",
    keysToggle: "Show or hide this panel",
    keysSkip: "Skip the open item",
    keysYesNo: "Answer Yes / No (open item)",
    keysRate: "Answer Pass / Fail / N/A (open item)",
    keysPrevNext: "Previous / next item or group",
    timelineStatusScheduled: "Scheduled",
    timelineStatusCreated: "Created",
    timelineAutomatic: "Automatic",
    timelineUpdated: "Last updated",
    timelineCancelled: "Inspection cancelled",
    timelineApproved: "Report approved",
    timelineCompleted: "Inspection completed",
    timelineResumeScheduled: "Resume scheduled",
    timelinePaused: "Inspection paused",
    timelineStarted: "Inspection started",
    timelineCreated: "Inspection created",
    exportReport: "Export report",
    report: "Report",
    askAiDescription: "Get help with the next checks, based on your answers so far.",
    askAiTooltip: "AI can help with the next checks",
    askAi: "Ask AI",
    saveAndExit: "Save & exit",
    savedFindInWork: "Saved. Find it in Work under Mine to carry on.",
    delete: "Delete",
    deleteBody: "This can’t be undone.",
    deleteTitle: "Delete this inspection?",
    allItemsDone: "All items done. Tap Complete inspection.",
    sharedReportHint: "If someone shared a report with you, open the link in their email. You can also check Activity for reports waiting for you.",
    requester: "Requester",
    fromRequest: "From request",
    unknownStatus: "Unknown",
    itemsHidden: "{{total}} items no longer apply, so they’re hidden.",
    itemHiddenOne: "“{{label}}” no longer applies, so it’s hidden.",
    addNoteFirstBody: "Type a short note, then tap Polish.",
    addNoteFirstTitle: "Add a note first",
    signInVideos: "Sign in to add videos.",
    signInPhotos: "Sign in to add photos.",
    signInTitle: "Sign in required",
    recordVideo: "Record or choose a video",
    takePhoto: "Take or choose a photo",
    polishFailedTitle: "Couldn’t polish the text",
    polishedTitle: "Polished wording",
    discard: "Discard",
    editDraft: "Edit",
    accept: "Accept",
    revert: "Revert",
    draftNoteTitle: "Draft note for {{label}}",
    previousInspection: "Previous inspection",
    previousRating: "Previous: {{rating}}",
    previousNote: "Last time: {{note}}",
    videoOfA11y: "Video {{index}} of {{total}}",
    photoOfA11y: "Photo {{index}} of {{total}}",
    expandDetails: "Expand details",
    collapseDetails: "Collapse details",
    collapsed: "collapsed",
    expanded: "expanded",
    stateNotCompletedShort: "not completed",
    stateSkippedShort: "skipped",
    stateCompletedShort: "completed",
    itemHeaderA11y: "{{label}}, {{state}}, {{expanded}}",
    stateNotCompleted: "Not completed",
    stateCompleted: "Item completed",
    stateSkipped: "Skipped",
    followUpAddA11y: "Add a follow-up for {{label}}",
    followUpEditA11y: "Edit the follow-up for {{label}}",
    flaggedNeedsJob: "Flagged. Does this need a job?",
    mediaAddA11y: "Add a photo or video for {{label}}",
    mediaAttachedA11y: "Add evidence for {{label}}, {{total}} attached",
    noteAddA11y: "Add or dictate a note for {{label}}",
    noteEditA11y: "Edit the note for {{label}}",
    note: "Note",
    naA11y: "Not applicable",
    naSub: "Doesn’t apply here",
    na: "N/A",
    flagSub: "Needs a second look",
    flag: "Flag",
    failSub: "Does not meet criteria",
    fail: "Fail",
    passSub: "Meets inspection criteria",
    pass: "Pass",
    media: "Media",
    followUp: "Follow-up",
    followUpProposed: "Job proposed",
    yesOrNo: "Yes or No",
    passFailNa: "Pass, Fail or N/A",
    passFlagFail: "Pass, Flag or Fail",
    level: "Level",
    conditionRating: "Condition Rating",
    stop: "Stop",
    finishing: "Finishing...",
    dictate: "Dictate",
    polishing: "Polishing…",
    generating: "Generating…",
    polish: "Polish",
    generateDraft: "Generate draft",
    previous: "Previous",
    completeInspection: "Complete inspection",
    nextItem: "Next item",
    lastItem: "Last item",
    nextGroup: "Next group",
    lastGroup: "Last group",
    nextUnanswered: "Next unanswered",
    started: "Started {{when}}",
    startedBy: "Started {{when}} · {{name}}",
  },
  viewModel: {
    completionHintBlocking: 'Complete inspection will appear once required items are answered or skipped.',
    completionHintFinalGroup: 'Complete inspection will appear when you reach the final group.',
    completionHintLastItem: 'Complete inspection will appear on the last item.',
    completionHintOptionalRemain: 'Optional checklist items remain — you can finish after required items are done.',
  },
  pendingMedia: {
    failedCaption_one: '{{count}} not uploaded — tap to retry',
    failedCaption_other: '{{count}} not uploaded — tap to retry',
    pendingCaption_one: '{{count}} saved on this device, waiting to upload',
    pendingCaption_other: '{{count}} saved on this device, waiting to upload',
    retryA11y: 'Retry uploading this capture',
    pendingA11y: 'Capture saved on this device, waiting to upload',
  },
  sideProgressPanel: {
    sections: 'Sections',
    noSections: 'No sections',
    general: 'General',
  },
  findingsReview: {
    notAnswered_other: "{{count}} not answered",
    notAnswered_one: "{{count}} not answered",
    failedNeedJob_other: "{{count}} failed items need a job",
    failedNeedJob_one: "{{count}} failed item needs a job",
    reportArchived: 'Report saved',
    findingsNeedDecision_one: '{{count}} failed item needs a job, or a reason to leave it.',
    findingsNeedDecision_other: '{{count}} failed items need a job, or a reason to leave them.',
    noFindingsNeedFollowUp: 'No findings need follow-up.',
    recommendedWorkPackages_one:
      '{{count}} job suggested from the findings.',
    recommendedWorkPackages_other:
      '{{count}} jobs suggested from the findings.',
    allClear: 'All inspected items are clear.',
    successfulItemsTitle: 'Successful items · {{count}}',
    successfulItemsSummary: 'Items that passed with nothing to action',
    notRated: 'Not rated',
    answerItem: "Re-open to answer",
    planWork: "Confirm job",
    decisionA11y: '{{label}}: {{itemLabel}}',
    uncertain: "Not answered",
    urgentFinding: "Urgent finding",
    failedFinding: "Failed item",
  },
  vision: {
    findingsGroup: "Additional findings",
    keepWorking: "Keep working",
    scan: {
      group: "Scan",
      item: "Scan",
      groupHint: "Vision can help with {{count}} of {{total}} checks",
      recommendedHint: "Recommended: scan this section first",
      resultsReady: "Results ready",
      analysing: "Analysing…",
      open: "Open scan",
      removeFinding: "Remove finding",
      supportedByEvidence: "Supported by visual evidence",
      cameraAssisted: "Camera-assisted, no photo kept",
    },
    walk: {
      cardActionUpload: "Add photos",
      cardTitleUpload: "Check the room from photos",
      cardTitle: "Walk round with the camera",
      cardBody: "Vision can answer {{count}} of {{total}} checks from what it sees and fills them in. You only check what it couldn’t see or wasn’t sure of.",
      cardAction: "Start walk-round",
      howTo: "Tap the red button, then walk slowly and pause briefly at each item on the checklist.",
      walking: "Scanning… walk slowly and pause at each item. Tap Finish when you have been round.",
      start: "Start walk-round",
      stop: "Pause walk-round",
      uploadTitle: "Add photos from round the room",
      autoFilled: "Filled in for you: {{count}}",
      rushedTitle: "That was quick",
      rushedBody: "You covered {{total}} items in {{taken}} s. Pause about 2 seconds at each one (around {{suggested}} s in all) so Vision gets a sharp view of everything.",
      keepScanning: "Keep scanning",
      finishAnyway: "Finish anyway",
      slowDown: "Slow down and hold the phone steady",
    },
    capture: {
      title: "Scan",
      uploadTitle: "Add photos to scan",
      manual: "Answer manually",
      moveSlowly: "Take a few clear photos of what this section checks.",
      damageTip: "Get close and shoot along the panel at a low angle — dents show in the reflections.",
      checklistToggle: "What to capture · {{done}}/{{total}}",
      checklistToggleNoCount: "What to capture",
      found: "found",
      removeFrameA11y: "Remove photo {{index}}",
      lowLight: "More light needed",
      limitReached: "Photo limit reached. Remove one to add another.",
      costHint_one: "Analysing uses {{count}} credit · {{balance}} available",
      costHint_other: "Analysing uses {{count}} credits · {{balance}} available",
      shutterA11y: "Take photo",
      finishCount: "Finish scan ({{count}})",
      cameraBlocked: "Camera access is turned off. Turn it on in Settings, or add photos instead.",
      cameraNeeded: "Allow camera access to scan, or add photos instead.",
      openSettings: "Open settings",
      grant: "Allow camera",
      webNoLocal: "Photos are checked by AssetWRX in the cloud here. You review everything before it is saved.",
      upload: "Upload photos",
      useCamera: "Use camera",
      fromPhotos: "Add from photos",
      failed: "Couldn’t add that photo. Try again.",
      seedFailed: "Couldn’t open the photo. Take a new one or add one from your photos.",
    },
    stage: {
      reading_labels: "Reading labels…",
      checking_items: "Checking inspection items…",
      reviewing_condition: "Reviewing visible condition…",
      preparing_results: "Preparing results…",
    },
    coverage: {
      observed: "Observed",
      partial: "Partly seen",
      not_observed: "Not observed",
    },
    cloud: {
      offline: "You are offline, so only on-device results are shown.",
      switched_off: "Cloud analysis is switched off for this workspace. Only what the device could read is shown.",
      nothing_to_scan: "Nothing in this section can be read from a photo. Answer the checks yourself.",
      no_credits: "Not enough AI credits for cloud analysis. Only what the device could read is shown.",
      timeout: "Cloud analysis took too long. Only what the device could read is shown.",
      blocked_content: "These photos could not be analysed in the cloud. Only what the device could read is shown.",
      failed: "Cloud analysis did not complete. Only what the device could read is shown.",
      analyseNow: "Analyse with AI now · {{cost}}",
      retry: "Try cloud analysis again",
    },
    review: {
      title: "Review suggestions",
      emptyTitle: "Couldn’t determine these checks from the scan",
      emptyBody: "Scan again with the item in clear view, or answer the checks yourself.",
      summaryReady: "Ready: {{count}}",
      summaryCheck: "To check: {{count}}",
      summaryNotSeen: "Not seen: {{count}}",
      summaryIssues: "Possible issues: {{count}}",
      frameA11y: "Scan photo {{index}}",
      readyTitle: "Ready to confirm",
      checkTitle: "Check these",
      notObservedTitle: "Not observed",
      issuesTitle: "Additional issues",
      coverageTitle: "What the scan covered",
      acceptReady: "Accept ready answers ({{count}})",
      bulkSaved: "Answers saved: {{count}}",
      undo: "Undo",
      done: "Done",
      scanAgain: "Scan again",
      completeManually: "Answer manually",
      leaveTitle: "Leave without reviewing?",
      leaveBody: "Some suggestions from this scan haven't been reviewed. If you leave now, they're discarded.",
      keepReviewing: "Keep reviewing",
      discard: "Discard",
      observed: "Observed",
      suggested: "Suggested",
      differs: "Differs from your answer. Accepting replaces it.",
      partial: "Only partly in view. Check before accepting.",
      conflict: "The readings disagree. Choose the correct value.",
      previousReading: "Last recorded: {{value}}",
      backwards: "Lower than the last recorded reading.",
      unitMismatch: "This meter is recorded in {{unit}}.",
      serialMismatch: "Doesn’t match this asset’s recorded serial.",
      accept: "Accept",
      change: "Change",
      reject: "Reject",
      save: "Save",
      possibleIssue: "Possible issue",
      confirmIssue: "Confirm issue",
      flagIssue: "Flag for review",
      noIssue: "No issue",
      decision: {
        accepted: "Saved",
        changed: "Saved with your change",
        rejected: "Not used",
        fail: "Added to Additional findings",
        flag: "Flagged in Additional findings",
        dismissed: "Dismissed",
      },
      reason: {
        not_observed: "Not visible in the photos.",
        partial: "Only partly visible.",
        low_confidence: "Not clear enough to suggest an answer.",
        no_evidence: "Nothing in the scan answers this.",
      },
    },
    defect: {
      corrosion: "Corrosion",
      leak: "Leak",
      crack: "Crack",
      wear: "Wear",
      deformation: "Deformation",
      missing_component: "Missing component",
      loose_component: "Loose component",
      damage: "Damage",
      contamination: "Contamination",
      damp_staining: "Damp staining",
      mould_growth: "Mould-like growth",
      blockage: "Blockage",
      overheating: "Overheating",
      dent: "Dent",
      scratch: "Scratch",
      paint_damage: "Paint damage",
      glazing_damage: "Glass damage",
      light_damage: "Light damage",
      tyre_damage: "Tyre damage",
      cable_damage: "Cable damage",
      exposed_wiring: "Exposed wiring",
      misalignment: "Misalignment",
      blocked_access: "Blocked access",
      other_visible_anomaly: "Something that needs a look",
    },
    trainingShare: {
      title: "Vision improvement data",
      body: "Vision keeps a record of which suggestions your team accepted, changed or rejected, without photos, to measure its accuracy. This setting is on by default: the evidence photos your team kept for those suggestions are also used to train Vision. They are copied to Google Cloud in the EU for training and deleted from there within 30 days. Turning it off stops any further use; a model already trained with them is not un-trained. Records are kept for 24 months.",
      toggle: "Use our evidence photos to improve Vision",
      on: "On. Evidence photos behind reviewed suggestions may be copied to Google Cloud (EU) to train Vision.",
      off: "Off. Only the review decisions are kept, without photos.",
      failed: "Could not change this setting. Try again.",
      retry: "Retry",
    },
  },
  ratingScale: {
    poor: "Poor",
    fair: "Fair",
    good: "Good",
    veryGood: "Very Good",
    excellent: "Excellent",
    low: "Low",
    quarter: "Quarter",
    half: "Half",
    threeQuarter: "Three quarter",
    full: "Full",
  },
} as const;


